import type { LoadModelCtx } from "./modelsApi"
import type { ModelModules, RegisterModelsOptions } from "./registerModels"
import type * as frameworkSchemas from "./models"
import { models } from "./modelsApi"
import { registerModels } from "./registerModels"


type ExtractModelNamesFromSchemas<TModules> = {
  [K in keyof TModules]-?: K extends `${infer Name}Schema` ? Name : never
}[keyof TModules] & string

type FrameworkModelName = ExtractModelNamesFromSchemas<typeof frameworkSchemas>

type RpcbaseGlobalModelName = Extract<FrameworkModelName, "RBUser" | "RBTenant" | "RBOAuthRequest">

export type TenantModelName<TTenantModules extends ModelModules> =
  ExtractModelNamesFromSchemas<TTenantModules>

export type GlobalModelName<TGlobalModules extends ModelModules> =
  | RpcbaseGlobalModelName
  | ExtractModelNamesFromSchemas<TGlobalModules>

type LoadedTenantModel = Awaited<ReturnType<(typeof models)["get"]>>
type LoadedGlobalModel = Awaited<ReturnType<(typeof models)["getGlobal"]>>

type ModelsGetFn<TTenantModules extends ModelModules> = {
  (
    modelName: TenantModelName<TTenantModules>,
    ctx: LoadModelCtx,
  ): ReturnType<(typeof models)["get"]>
  <const TNames extends readonly TenantModelName<TTenantModules>[]>(
    modelNames: TNames,
    ctx: LoadModelCtx,
  ): Promise<{ [K in keyof TNames]: LoadedTenantModel }>
}

type ModelsGetUnsafeFn<TTenantModules extends ModelModules> = {
  (modelName: TenantModelName<TTenantModules>, ctx: LoadModelCtx): ReturnType<(typeof models)["getUnsafe"]>
  <const TNames extends readonly TenantModelName<TTenantModules>[]>(
    modelNames: TNames,
    ctx: LoadModelCtx,
  ): Promise<{ [K in keyof TNames]: LoadedTenantModel }>
}

type ModelsGetGlobalFn<TGlobalModules extends ModelModules> = {
  (modelName: GlobalModelName<TGlobalModules>, ctx: LoadModelCtx): ReturnType<(typeof models)["getGlobal"]>
  <const TNames extends readonly GlobalModelName<TGlobalModules>[]>(
    modelNames: TNames,
    ctx: LoadModelCtx,
  ): Promise<{ [K in keyof TNames]: LoadedGlobalModel }>
}

export type ModelsConfig<TTenantModules extends ModelModules, TGlobalModules extends ModelModules> = {
  tenant: TTenantModules
  global?: TGlobalModules
}

export type ModelsClient<TTenantModules extends ModelModules, TGlobalModules extends ModelModules> = {
  register: (modules: ModelsConfig<TTenantModules, TGlobalModules>, options?: RegisterModelsOptions) => void
  get: ModelsGetFn<TTenantModules>
  getUnsafe: ModelsGetUnsafeFn<TTenantModules>
  getGlobal: ModelsGetGlobalFn<TGlobalModules>
}

export const createModels = <
  TTenantModules extends ModelModules,
  TGlobalModules extends ModelModules = Record<string, never>,
>(
    modules: ModelsConfig<TTenantModules, TGlobalModules>,
    options?: RegisterModelsOptions,
  ): ModelsClient<TTenantModules, TGlobalModules> => {
  registerModels(modules, options)

  const get = (async (
    modelNameOrNames: TenantModelName<TTenantModules> | readonly TenantModelName<TTenantModules>[],
    ctx: LoadModelCtx,
  ) => {
    if (Array.isArray(modelNameOrNames)) {
      return Promise.all(modelNameOrNames.map((modelName) => models.get(modelName as string, ctx)))
    }
    return models.get(modelNameOrNames as string, ctx)
  }) as ModelsClient<TTenantModules, TGlobalModules>["get"]

  const getUnsafe = (async (
    modelNameOrNames: TenantModelName<TTenantModules> | readonly TenantModelName<TTenantModules>[],
    ctx: LoadModelCtx,
  ) => {
    if (Array.isArray(modelNameOrNames)) {
      return Promise.all(modelNameOrNames.map((modelName) => models.getUnsafe(modelName as string, ctx)))
    }
    return models.getUnsafe(modelNameOrNames as string, ctx)
  }) as ModelsClient<TTenantModules, TGlobalModules>["getUnsafe"]

  const getGlobal = (async (
    modelNameOrNames: GlobalModelName<TGlobalModules> | readonly GlobalModelName<TGlobalModules>[],
    ctx: LoadModelCtx,
  ) => {
    if (Array.isArray(modelNameOrNames)) {
      return Promise.all(modelNameOrNames.map((modelName) => models.getGlobal(modelName as string, ctx)))
    }
    return models.getGlobal(modelNameOrNames as string, ctx)
  }) as ModelsClient<TTenantModules, TGlobalModules>["getGlobal"]

  return {
    register: (nextModules, nextOptions) => registerModels(nextModules, nextOptions),
    get,
    getUnsafe,
    getGlobal,
  }
}
